import moment from "moment";
import { useEffect, useRef, useState } from "react";
import {
  getFileDownloadUrl,
  getFileMetadata,
} from "../../../api/firebase/firestoreService";

interface UseTextNoteScreenshotReturn {
  screenshotUrl: string;
  loading: boolean;
  error: string | null;
  metadata: {
    timeCreated: string | null;
    updated: string | null;
  } | null;
  formattedTime: string;
}

export const useTextNoteScreenshot = (
  identifier: string | null,
): UseTextNoteScreenshotReturn => {
  const [screenshotUrl, setScreenshotUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<{
    timeCreated: string | null;
    updated: string | null;
  } | null>(null);
  const isMountedRef = useRef<boolean>(true);

  useEffect(() => {
    // Reset mounted flag on effect run
    isMountedRef.current = true;

    const fetchScreenshot = async () => {
      if (!identifier) {
        // Only update state if still mounted
        if (isMountedRef.current) {
          setScreenshotUrl("");
          setLoading(false);
          setError(null);
          setMetadata(null);
        }
        return;
      }

      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const filePath = `onco_emr/${identifier}/onco_emr_text_note_screenshot/text_notes_stats.png`;
        const [downloadUrl, fileMetadata] = await Promise.all([
          getFileDownloadUrl(filePath),
          getFileMetadata(filePath),
        ]);

        // Only update state if component is still mounted and identifier hasn't changed
        if (isMountedRef.current) {
          if (downloadUrl) {
            setScreenshotUrl(downloadUrl);
            setMetadata(fileMetadata);
            setError(null);
          } else {
            setError("Failed to fetch screenshot URL");
            setScreenshotUrl("");
            setMetadata(null);
          }
        }
      } catch (err) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          console.error("Error fetching text note screenshot:", err);
          setError("Error fetching screenshot");
          setScreenshotUrl("");
          setMetadata(null);
        }
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchScreenshot();

    // Cleanup function to prevent state updates after unmount or identifier change
    return () => {
      isMountedRef.current = false;
    };
  }, [identifier]);

  // Format the time in the same way as useFinalScreenshotFromSlice
  const formattedTime = metadata?.updated
    ? moment
        .utc(metadata.timeCreated)
        .add(5, "hours")
        .add(30, "minutes")
        .format("MMM DD, YYYY h:mm A")
    : "";

  return { screenshotUrl, loading, error, metadata, formattedTime };
};
